/**
 * Variant Quantity Limit
 * 
 * Prevents adding more than the specified quantity of the same variant to the cart.
 */

(function() {
  'use strict';

  // Get theme settings with fallback
  const isEnabled = Theme?.settings?.variant_quantity_limit_enabled || false;
  const limit = parseInt(Theme?.settings?.variant_quantity_limit) || 1;

  if (!isEnabled) {
    return;
  }

  // Track variants in cart
  let cartVariants = new Map();
  
  // Track processed forms to avoid duplicate listeners
  let processedForms = new Set();
  
  // Debounce timer for DOM updates
  let updateTimeout;

  // Get current cart data
  async function getCartData() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Reset cart variants map
      cartVariants.clear();
      
      // Track each variant in cart
      cart.items.forEach(item => {
        const variantId = item.variant_id.toString();
        cartVariants.set(variantId, (cartVariants.get(variantId) || 0) + item.quantity);
      });
      
      return cart;
    } catch (error) {
      console.error('Error fetching cart data:', error);
      return null;
    }
  }

  // Check if variant can be added
  function canAddVariant(variantId) {
    const currentQuantity = cartVariants.get(variantId.toString()) || 0;
    return currentQuantity < limit;
  }

  // Override quantity input max attribute
  function updateQuantityInputs() {
    const quantityInputs = document.querySelectorAll('.quantity-selector input[type="number"]');
    
    quantityInputs.forEach(input => {
      if (input.max) {
        input.max = Math.min(parseInt(input.max), limit);
      } else {
        input.max = limit;
      }
    });
  }

  // Show error message
  function showErrorMessage(form) {
    const errorDiv = form.querySelector('.variant-limit-error');
    if (errorDiv) {
      errorDiv.classList.remove('hidden');
    }
  }

  // Hide error message
  function hideErrorMessage(form) {
    const errorDiv = form.querySelector('.variant-limit-error');
    if (errorDiv) {
      errorDiv.classList.add('hidden');
    }
  }

  // Handle button click
  function handleButtonClick(e) {
    const form = e.target.closest('form');
    const variantId = form.querySelector('[name="id"]')?.value;
    
    if (variantId && !canAddVariant(variantId)) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      showErrorMessage(form);
      
      // Disable the button temporarily
      const addToCartButton = form.querySelector('.add-to-cart-button');
      if (addToCartButton) {
        addToCartButton.disabled = true;
        
        // Re-enable after 5 seconds
        setTimeout(() => {
          addToCartButton.disabled = false;
          hideErrorMessage(form);
        }, 5000);
      }
      
      return false;
    }
  }

  // Add form validation
  function addFormValidation() {
    const productForms = document.querySelectorAll('form[action*="/cart/add"]');
    
    productForms.forEach((form) => {
      // Skip if already processed
      if (processedForms.has(form)) return;
      
      // Add click handler to add-to-cart buttons
      const submitButtons = form.querySelectorAll('.add-to-cart-button');
      submitButtons.forEach(button => {
        button.addEventListener('click', handleButtonClick, true);
      });
      
      processedForms.add(form);
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  async function init() {
    // Load initial cart data
    await getCartData();
    
    updateQuantityInputs();
    addFormValidation();
    
    // Update inputs when new content is added (with debounce)
    const observer = new MutationObserver(function(mutations) {
      // Clear existing timeout
      if (updateTimeout) {
        clearTimeout(updateTimeout);
      }
      
      // Debounce updates to prevent infinite loops
      updateTimeout = setTimeout(() => {
        updateQuantityInputs();
        addFormValidation();
      }, 100);
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Listen for cart events
    const cartEvents = [
      'cart:update',
      'quantity-selector:update'
    ];
    
    cartEvents.forEach(eventName => {
      document.addEventListener(eventName, async (event) => {
        await getCartData();
      });
    });
    
    // Direct click listener for remove buttons
    document.addEventListener('click', function(event) {
      if (event.target.closest('.cart-items__remove')) {
        // Wait a bit for the cart to update, then refresh our data
        setTimeout(() => {
          getCartData();
        }, 500);
      }
    });
    
    // Fallback polling every 30 seconds
    setInterval(async () => {
      await getCartData();
    }, 30000);
  }
})();